// src/pages/ImagePreparation/ImagePreparation.jsx
// E-ticaret ürün görsel hazırlama modülü

import React, { useState, useRef, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
    IMAGE_SIZE_PRESETS,
    GEMINI_MODELS,
    DEFAULT_GEMINI_MODEL,
    editImageWithGemini,
    resizeImage,
    downloadImage
} from '../../gemini';
import { CLOUDINARY_UPLOAD_URL, cloudinaryConfig } from '../../cloudinary';

// IndexedDB Helper for Large Image Storage
const DB_NAME = 'ImageGenStudioDB';
const STORE_NAME = 'history';

const initDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };
        request.onsuccess = (e) => resolve(e.target.result);
        request.onerror = (e) => reject(e.target.error);
    });
};

const saveHistoryToDB = async (history) => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        // Clear old and save new
        await new Promise((resolve, reject) => {
            const clearReq = store.clear();
            clearReq.onsuccess = resolve;
            clearReq.onerror = reject;
        });

        for (const item of history) {
            store.add(item);
        }
    } catch (err) {
        console.error('IndexedDB save error:', err);
    }
};

const loadHistoryFromDB = async () => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        return new Promise((resolve) => {
            const request = store.getAll();
            request.onsuccess = () => {
                // Sort by timestamp descending
                const data = request.result || [];
                resolve(data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
            };
            request.onerror = () => resolve([]);
        });
    } catch (err) {
        console.error('IndexedDB load error:', err);
        return [];
    }
};

// Styles
const styles = {
    container: {
        padding: '24px',
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        marginBottom: '32px',
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1a1d29',
        margin: 0,
        marginBottom: '8px',
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    // Tabs
    tabContainer: {
        display: 'flex',
        gap: '4px',
        marginBottom: '24px',
        borderBottom: '1px solid #e2e8f0',
    },
    tab: {
        padding: '12px 24px',
        cursor: 'pointer',
        fontWeight: '600',
        color: '#64748b',
        borderBottom: '2px solid transparent',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    activeTab: {
        color: '#06C167',
        borderBottom: '2px solid #06C167',
    },
    subTabContainer: {
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        padding: '4px',
        background: '#f1f5f9',
        borderRadius: '12px',
        width: 'fit-content',
    },
    subTab: {
        padding: '8px 16px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    activeSubTab: {
        background: 'white',
        color: '#0f172a',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    },
    // Grid & Layout
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '24px',
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
    },
    cardTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1a1d29',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    // Upload Area
    uploadArea: {
        border: '2px dashed #d1d5db',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
        background: '#f9fafb',
        position: 'relative',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    uploadAreaActive: {
        borderColor: '#06C167',
        background: '#f0f4ff',
    },
    uploadIcon: {
        fontSize: '48px',
        marginBottom: '12px',
    },
    uploadText: {
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '8px',
    },
    uploadButton: {
        display: 'inline-block',
        padding: '10px 20px',
        background: 'linear-gradient(135deg, #06C167, #764ba2)',
        color: 'white',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        border: 'none',
    },
    previewImage: {
        width: '100%',
        maxHeight: '400px',
        objectFit: 'contain',
        borderRadius: '8px',
    },
    removeImageBtn: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'rgba(239, 68, 68, 0.9)',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        zIndex: 5,
    },
    // Form Elements
    textArea: {
        width: '100%',
        minHeight: '100px',
        padding: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        resize: 'vertical',
        fontFamily: 'inherit',
        outline: 'none',
        transition: 'border-color 0.2s',
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151',
        marginBottom: '8px',
    },
    input: {
        width: '100%',
        padding: '10px 14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
    },
    // Size Selection
    sizeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: '10px',
        marginBottom: '16px',
    },
    sizeChip: {
        padding: '10px',
        borderRadius: '8px',
        border: '2px solid #e2e8f0',
        background: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'center',
    },
    sizeChipSelected: {
        borderColor: '#06C167',
        background: '#f0f4ff',
    },
    sizeChipName: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#1a1d29',
    },
    sizeChipDimensions: {
        fontSize: '11px',
        color: '#6b7280',
        marginTop: '2px',
    },
    // Quick Actions
    quickActionGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
        gap: '12px',
    },
    quickActionCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        padding: '16px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        background: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'center',
    },
    quickActionIcon: {
        fontSize: '24px',
    },
    quickActionLabel: {
        fontSize: '12px',
        fontWeight: '500',
        color: '#374151',
    },
    // Comparison Slider
    sliderContainer: {
        position: 'relative',
        width: '100%',
        maxWidth: '800px',
        aspectRatio: '1/1',
        overflow: 'hidden',
        borderRadius: '12px',
        background: '#eee',
        border: '1px solid #e2e8f0',
    },
    sliderImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        pointerEvents: 'none',
    },
    sliderBefore: {
        zIndex: 1,
    },
    sliderAfter: {
        zIndex: 2,
    },
    sliderRange: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        appearance: 'none',
        background: 'transparent',
        zIndex: 3,
        cursor: 'ew-resize',
        margin: 0,
    },
    sliderLine: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: '2px',
        background: 'white',
        boxShadow: '0 0 10px rgba(0,0,0,0.5)',
        zIndex: 2,
        pointerEvents: 'none',
    },
    sliderHandle: {
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '32px',
        height: '32px',
        background: 'white',
        borderRadius: '50%',
        boxShadow: '0 0 10px rgba(0,0,0,0.3)',
        zIndex: 2,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        color: '#06C167',
    },
    // General UI
    generateButton: {
        width: '100%',
        padding: '16px',
        background: 'linear-gradient(135deg, #06C167, #764ba2)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        marginTop: '20px',
        transition: 'opacity 0.2s',
    },
    generateButtonDisabled: {
        opacity: 0.6,
        cursor: 'not-allowed',
    },
    alert: {
        padding: '16px',
        borderRadius: '10px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
    },
    alertWarning: { background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e' },
    alertError: { background: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b' },
    alertSuccess: { background: '#d1fae5', border: '1px solid #10b981', color: '#065f46' },

    // Results
    resultsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px',
        marginTop: '20px',
    },
    resultCard: {
        background: '#f9fafb',
        borderRadius: '12px',
        padding: '12px',
        border: '1px solid #e2e8f0',
    },
    resultImage: {
        width: '100%',
        height: '200px',
        objectFit: 'contain',
        borderRadius: '8px',
        background: 'white',
        marginBottom: '10px',
        cursor: 'pointer',
    },
    resultActions: {
        display: 'flex',
        gap: '6px',
    },
    actionButton: {
        flex: 1,
        padding: '8px',
        borderRadius: '6px',
        border: 'none',
        fontSize: '12px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
    downloadButton: { background: '#06C167', color: 'white' },
    cloudinaryButton: { background: '#10b981', color: 'white' },

    // Modal
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
        backdropFilter: 'blur(5px)',
    },
    modalContent: {
        background: 'transparent',
        maxWidth: '90vw',
        maxHeight: '90vh',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    modalImage: {
        maxWidth: '100%',
        maxHeight: '80vh',
        borderRadius: '8px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    },
    closeButton: {
        position: 'absolute',
        top: '-40px',
        right: '0',
        background: 'rgba(255,255,255,0.2)',
        border: 'none',
        color: 'white',
        fontSize: '24px',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Loaders
    loadingOverlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999,
    },
    loadingCard: {
        background: 'white',
        borderRadius: '16px',
        padding: '30px',
        textAlign: 'center',
        maxWidth: '300px',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #e2e8f0',
        borderTopColor: '#06C167',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px',
    }
};

const spinnerKeyframes = `
  @keyframes spin { to { transform: rotate(360deg); } }
  input[type=range]::-webkit-slider-thumb { appearance: none; width: 0; height: 0; }
`;

// Example prompts
const QUICK_ACTIONS = [
    { icon: '✂️', label: 'Arka Planı Sil', prompt: 'Remove background, make it pure white.' },
    { icon: '💡', label: 'Stüdyo Işığı', prompt: 'Add studio lighting, soft shadows, professional product photography.' },
    { icon: '⚫', label: 'Doğal Gölge', prompt: 'Add a natural drop shadow underneath the product.' },
    { icon: '🔍', label: 'Keskinleştir', prompt: 'Increase sharpness, enhance details, high resolution.' },
    { icon: '🎨', label: 'Canlı Renkler', prompt: 'Enhance colors, make them pop, vibrant look.' },
    { icon: '⬜', label: 'Beyaz Zemin', prompt: 'Place on a clean white background, center the product.' }
];

function ImagePreparation() {
    const { userData } = useAuth();

    // State
    const [mainTab, setMainTab] = useState('studio'); // 'studio' | 'history'
    const [editMode, setEditMode] = useState('free'); // 'free' | 'quick' | 'color' | 'reference'

    // Inputs
    const [apiKey, setApiKey] = useState('');
    const [modelId, setModelId] = useState(DEFAULT_GEMINI_MODEL);
    const [sourceImage, setSourceImage] = useState(null);
    const [referenceImage, setReferenceImage] = useState(null); // For dual image
    const [prompt, setPrompt] = useState('');

    // Color Change State
    const [colorObject, setColorObject] = useState('');
    const [targetColor, setTargetColor] = useState('');

    // Size Selection
    const [selectedSizes, setSelectedSizes] = useState(['instagram-post']);
    const [useCustomSize, setUseCustomSize] = useState(false);
    const [customWidth, setCustomWidth] = useState(1080);
    const [customHeight, setCustomHeight] = useState(1080);

    // Comparison Slider State
    const [sliderPos, setSliderPos] = useState(50);
    const [showSlider, setShowSlider] = useState(false);
    const [sliderActiveResult, setSliderActiveResult] = useState(null);

    // Process State
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStatus, setProcessingStatus] = useState('');
    const [results, setResults] = useState([]); // Current session results
    const [history, setHistory] = useState([]); // Persistent history
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [previewImage, setPreviewImage] = useState(null);

    const fileInputRef = useRef(null);
    const refInputRef = useRef(null);

    // Initial Load
    useEffect(() => {
        const fetchApiKey = async () => {
            if (!userData?.companyId) return;
            try {
                const docRef = doc(db, 'companies', userData.companyId, 'settings', 'apiKeys');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.geminiApiKey) setApiKey(data.geminiApiKey);
                    if (data.geminiModelId) setModelId(data.geminiModelId);
                }
            } catch (err) {
                console.error('API Error:', err);
            }
        };
        fetchApiKey();

        // Load History from IndexedDB
        const loadHistory = async () => {
            const data = await loadHistoryFromDB();
            setHistory(data);
        };
        loadHistory();
    }, [userData]);

    // Save history on change - Use IndexedDB instead of localStorage
    useEffect(() => {
        if (history.length > 0) {
            saveHistoryToDB(history);
        }
    }, [history]);

    // Helpers
    const handleFileSelect = (e, setFunc) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (evt) => setFunc(evt.target.result);
            reader.readAsDataURL(file);
        }
    };

    const toggleSize = (id) => {
        setSelectedSizes(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const addToHistory = (newResults) => {
        const historyItems = newResults.map(r => ({
            ...r,
            timestamp: new Date().toISOString(),
            id: Date.now() + Math.random()
        }));
        setHistory(prev => [...historyItems, ...prev].slice(0, 30)); // Keep last 30
    };

    const handleGenerate = async () => {
        if (!sourceImage) return setError('Lütfen kaynak görsel yükleyin.');
        if (!apiKey) return setError('API anahtarı eksik. Ayarlardan tanımlayın.');

        let finalPrompt = prompt;
        if (editMode === 'quick' && !prompt) return setError('Lütfen bir işlem seçin.');
        if (editMode === 'color') {
            if (!colorObject || !targetColor) return setError('Nesne ve renk giriniz.');
            finalPrompt = `Change the color of ${colorObject} to ${targetColor}. Keep other details unchanged.`;
        }
        if (editMode === 'reference' && !referenceImage) return setError('Referans görsel yükleyin.');
        if (!finalPrompt.trim() && !referenceImage) return setError('Lütfen bir tarif girin.');

        setIsProcessing(true);
        setError('');
        setResults([]);

        const sizes = [];
        selectedSizes.forEach(id => {
            const preset = IMAGE_SIZE_PRESETS.find(p => p.id === id);
            if (preset) sizes.push(preset);
        });
        if (useCustomSize) sizes.push({ id: 'custom', name: 'Özel', width: Number(customWidth), height: Number(customHeight), aspectRatio: `${customWidth}:${customHeight}` });

        if (sizes.length === 0) {
            setIsProcessing(false);
            return setError('Boyut seçiniz.');
        }

        try {
            const sessionResults = [];
            for (const size of sizes) {
                setProcessingStatus(`${size.name} oluşturuluyor...`);
                // Use editImageWithGemini helper from gemini.js
                const aiResult = await editImageWithGemini(
                    sourceImage,
                    finalPrompt,
                    apiKey,
                    modelId,
                    size.aspectRatio,
                    editMode === 'reference' ? referenceImage : null
                );

                const finalImg = await resizeImage(aiResult.imageBase64, size.width, size.height);
                sessionResults.push({
                    ...size,
                    imageBase64: finalImg,
                    prompt: finalPrompt,
                    originalImage: sourceImage
                });
            }

            setResults(sessionResults);
            addToHistory(sessionResults);
            setSuccessMessage(`${sessionResults.length} görsel oluşturuldu.`);
            if (mainTab !== 'studio') setMainTab('studio');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleContinueEditing = (res) => {
        setSourceImage(res.imageBase64);
        setMainTab('studio');
        setSuccessMessage('Görsel düzenleme stüdyosuna taşındı. Yeni bir işlem seçebilirsiniz.');
        // Clear results to avoid confusion
        setResults([]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDownload = (res) => {
        downloadImage(res.imageBase64, `gen-${res.name}-${Date.now()}.png`);
    };

    const handleUploadToCloudinary = async (res) => {
        try {
            setProcessingStatus('Yükleniyor...');
            setIsProcessing(true);
            const blob = await (await fetch(res.imageBase64)).blob();
            const formData = new FormData();
            formData.append('file', blob);
            formData.append('upload_preset', cloudinaryConfig.upload_preset);

            const req = await fetch(CLOUDINARY_UPLOAD_URL, { method: 'POST', body: formData });
            const data = await req.json();
            if (data.secure_url) {
                setSuccessMessage(`Yüklendi: ${data.secure_url}`);
                window.open(data.secure_url, '_blank');
            } else throw new Error(data.error?.message || 'Upload failed');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    // --- RENDERERS ---

    const renderComparisonSlider = (original, modified) => (
        <div style={styles.sliderContainer}>
            {/* Original (Before) */}
            <img src={original} style={{ ...styles.sliderImage, ...styles.sliderBefore }} alt="Before" />

            {/* Modified (After) - Clipped by Slider Position */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                clipPath: `inset(0 0 0 ${sliderPos}%)`,
                zIndex: 2,
            }}>
                <img src={modified} style={{ ...styles.sliderImage, ...styles.sliderAfter }} alt="After" />
            </div>

            {/* Slider Elements */}
            <div style={{ ...styles.sliderLine, left: `${sliderPos}%` }}></div>
            <div style={{ ...styles.sliderHandle, left: `${sliderPos}%` }}>↔</div>

            <input
                type="range"
                min="0"
                max="100"
                value={sliderPos}
                onChange={(e) => setSliderPos(e.target.value)}
                style={styles.sliderRange}
            />

            <div style={{ position: 'absolute', bottom: '10px', left: '10px', background: 'rgba(0,0,0,0.5)', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', zIndex: 4 }}>ÖNCE (SOL)</div>
            <div style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(0,0,0,0.5)', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', zIndex: 4 }}>SONRA (SAĞ)</div>
        </div>
    );

    const renderStudioTab = () => (
        <div style={styles.grid}>
            {/* Left: Input */}
            <div style={styles.card}>
                <div style={styles.cardTitle}><span>📤</span> Kaynak Görsel</div>
                {!sourceImage ? (
                    <div style={styles.uploadArea} onClick={() => fileInputRef.current?.click()}>
                        <div style={styles.uploadIcon}>📷</div>
                        <div style={styles.uploadText}>Görsel Yükle</div>
                        <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={e => handleFileSelect(e, setSourceImage)} />
                    </div>
                ) : (
                    <div style={{ position: 'relative' }}>
                        <img src={sourceImage} style={styles.previewImage} alt="Source" />
                        <button style={styles.removeImageBtn} onClick={() => setSourceImage(null)}>×</button>
                    </div>
                )}

                {editMode === 'reference' && (
                    <div style={{ marginTop: '20px', borderTop: '1px dashed #e2e8f0', paddingTop: '20px' }}>
                        <div style={styles.cardTitle}><span>🖼️</span> Referans / Stil Görseli</div>
                        {!referenceImage ? (
                            <div style={{ ...styles.uploadArea, minHeight: '120px', padding: '20px' }} onClick={() => refInputRef.current?.click()}>
                                <div style={{ fontSize: '32px' }}>🎨</div>
                                <div style={styles.uploadText}>Stil veya Referans ekle</div>
                                <input ref={refInputRef} type="file" hidden accept="image/*" onChange={e => handleFileSelect(e, setReferenceImage)} />
                            </div>
                        ) : (
                            <div style={{ position: 'relative' }}>
                                <img src={referenceImage} style={styles.previewImage} alt="Reference" />
                                <button style={styles.removeImageBtn} onClick={() => setReferenceImage(null)}>×</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Right: Controls */}
            <div style={styles.card}>
                <div style={styles.subTabContainer}>
                    <div style={{ ...styles.subTab, ...(editMode === 'free' ? styles.activeSubTab : {}) }} onClick={() => setEditMode('free')}>✍️ Serbest</div>
                    <div style={{ ...styles.subTab, ...(editMode === 'quick' ? styles.activeSubTab : {}) }} onClick={() => setEditMode('quick')}>⚡ Hızlı</div>
                    <div style={{ ...styles.subTab, ...(editMode === 'color' ? styles.activeSubTab : {}) }} onClick={() => setEditMode('color')}>🎨 Renk</div>
                    <div style={{ ...styles.subTab, ...(editMode === 'reference' ? styles.activeSubTab : {}) }} onClick={() => setEditMode('reference')}>🖼️ Referans</div>
                </div>

                <div style={{ minHeight: '150px' }}>
                    {editMode === 'free' && (
                        <div>
                            <label style={styles.label}>Tarifiniz</label>
                            <textarea
                                style={styles.textArea}
                                value={prompt}
                                onChange={e => setPrompt(e.target.value)}
                                placeholder="Örn: Arka planı stüdyo grisi yap, ürünü parlat..."
                            />
                        </div>
                    )}

                    {editMode === 'quick' && (
                        <div style={styles.quickActionGrid}>
                            {QUICK_ACTIONS.map((action, i) => (
                                <div
                                    key={i}
                                    style={{ ...styles.quickActionCard, borderColor: prompt === action.prompt ? '#06C167' : '#e2e8f0' }}
                                    onClick={() => setPrompt(action.prompt)}
                                >
                                    <div style={styles.quickActionIcon}>{action.icon}</div>
                                    <div style={styles.quickActionLabel}>{action.label}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {editMode === 'color' && (
                        <div>
                            <div style={{ ...styles.alert, ...styles.alertWarning, marginBottom: '12px' }}>
                                💡 Görseldeki bir nesnenin rengini değiştirmek için nesneyi ve yeni rengi belirtin.
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={styles.label}>Hangi Nesne?</label>
                                <input style={styles.input} placeholder="Örn: Ayakkabı, Çanta, Arka Plan..." value={colorObject} onChange={e => setColorObject(e.target.value)} />
                            </div>
                            <div>
                                <label style={styles.label}>Yeni Renk?</label>
                                <input style={styles.input} placeholder="Örn: Kırmızı, Pastel Mavi, #ff0000..." value={targetColor} onChange={e => setTargetColor(e.target.value)} />
                            </div>
                        </div>
                    )}

                    {editMode === 'reference' && (
                        <div>
                            <div style={{ ...styles.alert, ...styles.alertWarning, marginBottom: '12px' }}>
                                💡 Yüklediğiniz referans görselin stilini veya içeriğini kaynak görsele uygulamak için tarif yazın.
                            </div>
                            <textarea
                                style={styles.textArea}
                                value={prompt}
                                onChange={e => setPrompt(e.target.value)}
                                placeholder="Örn: Bu ürünün stilini referans görseldeki gibi yap..."
                            />
                        </div>
                    )}
                </div>

                <div style={{ margin: '24px 0', borderTop: '1px solid #e2e8f0' }}></div>

                {/* Size Select */}
                <label style={styles.label}>Çıktı Boyutları</label>
                <div style={styles.sizeGrid}>
                    {IMAGE_SIZE_PRESETS.map(p => (
                        <div
                            key={p.id}
                            style={{ ...styles.sizeChip, ...(selectedSizes.includes(p.id) ? styles.sizeChipSelected : {}) }}
                            onClick={() => toggleSize(p.id)}
                        >
                            <div style={styles.sizeChipName}>{p.name}</div>
                            <div style={styles.sizeChipDimensions}>{p.width}x{p.height}</div>
                        </div>
                    ))}
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '12px' }}>
                    <input type="checkbox" checked={useCustomSize} onChange={e => setUseCustomSize(e.target.checked)} />
                    <span style={{ fontSize: '14px' }}>Özel Boyut</span>
                </label>
                {useCustomSize && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <input type="number" style={styles.input} value={customWidth} onChange={e => setCustomWidth(e.target.value)} placeholder="Genişlik" />
                        <span style={{ marginTop: '10px' }}>x</span>
                        <input type="number" style={styles.input} value={customHeight} onChange={e => setCustomHeight(e.target.value)} placeholder="Yükseklik" />
                    </div>
                )}

                <button style={{ ...styles.generateButton, opacity: isProcessing ? 0.7 : 1 }} onClick={handleGenerate} disabled={isProcessing}>
                    {isProcessing ? 'İşleniyor...' : '✨ Görselleri Oluştur'}
                </button>
            </div>
        </div>
    );

    const renderHistoryTab = () => (
        <div>
            <div style={styles.resultsGrid}>
                {history.length === 0 && <div style={{ color: '#6b7280', padding: '20px' }}>Henüz geçmiş kayıt yok.</div>}
                {history.map((item, i) => (
                    <div key={item.id || i} style={styles.resultCard}>
                        <img src={item.imageBase64} style={styles.resultImage} onClick={() => setPreviewImage(item)} alt="History" />
                        <div style={styles.resultTitle}>{item.name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px', height: '32px', overflow: 'hidden' }}>{item.prompt}</div>
                        <div style={styles.resultActions}>
                            <button style={{ ...styles.actionButton, ...styles.downloadButton }} onClick={() => handleDownload(item)} title="İndir">⬇️</button>
                            <button style={{ ...styles.actionButton, background: '#06C167', color: 'white' }} onClick={() => handleContinueEditing(item)} title="Düzenlemeye Devam Et">🔄</button>
                            <button style={{ ...styles.actionButton, background: '#ef4444', color: 'white' }} onClick={() => {
                                const newHistory = history.filter(h => h.id !== item.id);
                                setHistory(newHistory);
                            }} title="Sil">🗑️</button>
                            <button style={{ ...styles.actionButton, background: '#4b5563', color: 'white' }} onClick={() => {
                                setSliderActiveResult(item);
                                setShowSlider(true);
                            }} title="Karşılaştır">🆚</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderResults = () => {
        if (results.length === 0) return null;
        return (
            <div style={{ ...styles.card, marginTop: '24px', border: '2px solid #06C167' }}>
                <div style={styles.cardTitle}><span>🎉</span> Sonuçlar</div>
                <div style={styles.resultsGrid}>
                    {results.map((res, i) => (
                        <div key={i} style={styles.resultCard}>
                            <img src={res.imageBase64} style={styles.resultImage} onClick={() => setPreviewImage(res)} alt="Result" />
                            <div style={styles.resultTitle}>{res.name}</div>
                            <div style={styles.resultActions}>
                                <button style={{ ...styles.actionButton, ...styles.downloadButton }} onClick={() => handleDownload(res)}>⬇️ İndir</button>
                                <button style={{ ...styles.actionButton, background: '#06C167', color: 'white' }} onClick={() => handleContinueEditing(res)}>🔄 Düzenle</button>
                                <button style={{ ...styles.actionButton, background: '#4b5563', color: 'white' }} onClick={() => {
                                    setSliderActiveResult(res);
                                    setShowSlider(true);
                                }}>🆚 Karşılaştır</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            <style>{spinnerKeyframes}</style>

            <div style={styles.header}>
                <h1 style={styles.title}>🎨 AI Görsel Stüdyosu</h1>
                <p style={styles.subtitle}>Yapay zeka ile profesyonel ürün görselleri oluşturun, düzenleyin ve yönetin.</p>
                <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b' }}>
                    Model: <strong>{GEMINI_MODELS.find(m => m.id === modelId)?.name || modelId}</strong>
                </div>
            </div>

            {error && <div style={{ ...styles.alert, ...styles.alertError }}>⚠️ {error}</div>}
            {successMessage && <div style={{ ...styles.alert, ...styles.alertSuccess }}>✅ {successMessage}</div>}

            {/* Main Tabs */}
            <div style={styles.tabContainer}>
                <div style={{ ...styles.tab, ...(mainTab === 'studio' ? styles.activeTab : {}) }} onClick={() => setMainTab('studio')}>
                    <span>🎨</span> Stüdyo
                </div>
                <div style={{ ...styles.tab, ...(mainTab === 'history' ? styles.activeTab : {}) }} onClick={() => setMainTab('history')}>
                    <span>📜</span> Geçmiş
                </div>
            </div>

            {mainTab === 'studio' ? (
                <>
                    {renderStudioTab()}
                    {renderResults()}
                </>
            ) : renderHistoryTab()}

            {/* Global Loader */}
            {isProcessing && (
                <div style={styles.loadingOverlay}>
                    <div style={styles.loadingCard}>
                        <div style={styles.spinner}></div>
                        <h3 style={{ margin: '12px 0 8px 0' }}>İşleniyor...</h3>
                        <p style={{ color: '#666', fontSize: '14px' }}>{processingStatus}</p>
                    </div>
                </div>
            )}

            {/* Image Preview Modal */}
            {previewImage && (
                <div style={styles.modalOverlay} onClick={() => setPreviewImage(null)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <button style={styles.closeButton} onClick={() => setPreviewImage(null)}>✕</button>
                        <img src={previewImage.imageBase64} alt="Preview" style={styles.modalImage} />
                        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', background: 'white', padding: '12px', borderRadius: '12px' }}>
                            <button style={{ ...styles.actionButton, ...styles.downloadButton, width: '120px' }} onClick={() => handleDownload(previewImage)}>İndir</button>
                            <button style={{ ...styles.actionButton, ...styles.cloudinaryButton, width: '120px' }} onClick={() => handleUploadToCloudinary(previewImage)}>Buluta Yükle</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Comparison Slider Modal */}
            {showSlider && sliderActiveResult && (
                <div style={styles.modalOverlay} onClick={() => setShowSlider(false)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <button style={styles.closeButton} onClick={() => setShowSlider(false)}>✕</button>
                        <h3 style={{ color: 'white', marginBottom: '20px' }}>Öncesi / Sonrası Karşılaştırma</h3>
                        {renderComparisonSlider(sliderActiveResult.originalImage, sliderActiveResult.imageBase64)}
                        <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: '20px', fontSize: '14px' }}>Sürgüyü kaydırarak farkı inceleyin.</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImagePreparation;
